import React, { useCallback, useEffect, useMemo } from "react";
import { Footer } from "../";
import { selectFactory } from "../../../../../util/store/factory/constants";
import { getStore } from "../../../../../util/store/store";
import { ACTION_TYPE, EQUIPMENT_TYPE } from "../../../../../util/varibles/constants";
import { cloneObj } from "../../../../../util/varibles/global";
import { IFactory, IOperation, IOperations, IUnit, IVessel, TOperation } from "../../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../../util/varibles/message";
import { selectPlan } from "../../../constants";
import { getFishAmountOfUnit, getKeyUnit } from "../../../util";
import { ErrorName } from "../_components/ErrorOfOperation";
import SelectTime from "../_components/SelectTime";
import Units from "../_components/Units";
import { callEventEditOp, getEditOp, setEditOp, useEditOp } from "../EditOperationContext";
import ActivityLogs from "./ActivityLogs";
import { createHarvest, getRootUnit } from "./constants";
import Operations from "./Operations";
import styles from "./style.module.scss";

const ContainerFooter: React.FC = () => {
    const [ops, set] = useEditOp(state => state.harvests)
    const { loading, disabled } = useMemo(() => {
        return {
            loading: ops.some(item => item.loading),
            disabled: ops.some(item => Object.keys(item?.operation?.error_detail || {}).length > 0)
        }
    }, [ops]);

    const get = getEditOp();
    const { handleSave } = callEventEditOp();

    const handleSubmit = () => {
        const { editMode = ACTION_TYPE.CREATE, harvests } = get();
        let isError = false;
        const newHarvests = harvests.map(item => {
            const { error_detail = {} } = item.operation
            if (Object.keys(error_detail).length > 0) {
                isError = true;
                return item
            }

            const { sub_operations = [] } = item.operation || {};
            let isFishAmountEmpty = false;

            sub_operations.forEach((unit) => {
                const { delivery_types = [] } = unit;
                if (delivery_types.length === 0) {
                    const { site_name, unit_id } = unit;
                    error_detail[ErrorName.DeliveryTypes] = {
                        ...error_detail[ErrorName.DeliveryTypes] || {},
                        [getKeyUnit(unit)]: {
                            message: notify[NotifyCode.E44]([`${site_name}, ${unit_id}`])
                        }
                    }
                } else {
                    isFishAmountEmpty = isFishAmountEmpty || delivery_types.some(sub => {
                        const { fish_amount, total_weight } = sub;
                        return (!fish_amount && unit.avg_weight !== -1) || !total_weight
                    });
                }
            });
            if (isFishAmountEmpty) {
                const requiresFishAmount = sub_operations.some(unit =>
                    unit.avg_weight !== -1 && (unit.delivery_types || []).some(sub => !sub.fish_amount)
                );
                error_detail[ErrorName.FishAmount] = { message: notify[NotifyCode.E37]([requiresFishAmount ? 'Fish amount, total weight' : 'Total weight']) }
            }
            if (Object.keys(error_detail).length > 0) {
                isError = true;
            }
            return { ...item, operation: { ...item.operation, error_detail } };
        })
        if (isError) {
            set({ harvests: newHarvests });
            return;
        }
        const ops = harvests.reduce((rs: IOperations, item) => {
            rs[item.operation.id] = item;
            return rs;
        }, {})
        handleSave(editMode === ACTION_TYPE.CREATE ? { add: ops } : { update: ops })
    };

    return <div className="mt-auto">
        <Footer {...{
            loading,
            disableSave: disabled,
            handleSubmit: handleSubmit,
        }} />
    </div>
}

const Harvest: React.FC = () => {
    const set = setEditOp();
    const get = getEditOp();
    const { vessel } = get();

    const addOperation = useCallback((unit: IUnit, vessel: IVessel, code?: string) => {
        const { fish_amount, total_weight, inventory } = getFishAmountOfUnit(unit, vessel);
        const { count } = get();

        const value = {
            operation: createHarvest({
                unit,
                vessel,
                properties: {
                    operation_code: code || `${count + 1}`,
                    fish_amount,
                    total_weight,
                }
            }, get) as TOperation,
            isCall: true,
            activity_log: [],
            vessel
        }
        return { value, inventory };
    }, [get]);

    useEffect(() => {
        const { operation, vessel, activity_log = [], editMode } = get();
        if (!operation || !vessel)
            return;

        let { store = {} } = selectPlan.popupEdit(getStore()).values || {};

        const { sub_operations = [] } = operation || {};
        const factories = selectFactory.data(getStore());
        const factoriesFilter = sub_operations.reduce((list: IFactory[], item: any) => {
            const { factory_id, factory_name } = item;
            if (factory_id)
                return list.map(sub => sub.id === factory_id ? { ...sub, name: factory_name } : sub);
            return list;
        }, factories);

        set({
            factories: factoriesFilter,
            vesselTanks: (vessel.storages || []).filter(item => item.type === EQUIPMENT_TYPE.TANK),
        })

        let currentOps: ({ isCall?: boolean } & IOperation)[] = [];
        if (editMode === ACTION_TYPE.CREATE) {
            const units = cloneObj(store);
            if (factories.length === 0) {
                notify.error(NotifyCode.E18, "Create operation failed")();
            } else {
                currentOps = Object.keys(units).reduce((rs: typeof currentOps, key: string) => {
                    const { avg_weight } = units[key]
                    let count = 0, max = avg_weight > 0 ? 1 : 0;
                    while ((units[key].fish_amount > 0 || avg_weight < 0) && count <= max) {
                        const { avg_weight } = units[key]
                        const code = `${rs.length + 1}`;
                        const { value, inventory } = addOperation(units[key], vessel, code)
                        units[key].fish_amount = inventory;
                        units[key].total_weight = avg_weight > 0 ? inventory * avg_weight : 0;
                        rs = [...rs, value];
                        count++;
                    }
                    return rs;
                }, []);
            }
        } else {
            const error_detail: { [id: string]: { message: string } } = {};
            if (operation.total_weight > vessel.capacity)
                error_detail.total_weight = { message: notify[NotifyCode.E1]() };

            store = getRootUnit(operation);
            currentOps = [{
                operation: { ...operation, error_detail },
                isCall: operation.tanks.length === 0,
                activity_log,
                vessel
            }];
        }

        set({
            startTime: activity_log[0]?.est_start_time || Date.now(),
            units: cloneObj(store),
            count: currentOps.length,
            harvests: currentOps,
        });
    }, [set, addOperation, get]);

    return <div className={styles.container}>
        <Units
            keyData='harvest'
            gridTemplateColumns={"1fr 2fr 1fr 1fr 1fr 50px"}
            addOperation={unit => {
                if (!vessel)
                    return;
                const { value } = addOperation(unit, vessel);

                set(({ harvests: old, count: prevCount }) => ({ harvests: [...old, value], count: prevCount + 1 }));
            }}
            nameRow={(data: any) => <>
                <span title='Harvest ID'>{data.harvest_id}</span>
                <span title='Site name and unit ID'>{data.site_name}, {data.unit_id}</span>
            </>}
        />
        <div className={styles.ops}>
            <Operations />
            <div className="my-2">
                <SelectTime />
            </div>
            <ActivityLogs />
        </div>
        <ContainerFooter />
    </div>;
};

export default Harvest;
